/* Closing the book, and what the day was.
 *
 * Two steps in one sheet. The first asks — closing stops every clock still
 * running, which is not something to do by leaning on a button. The second is
 * the day itself: what was done, how fast it came in, and who was turned away
 * while it did. It is also what the button opens once the day is shut, so the
 * numbers are still there at ten o'clock when somebody asks.
 *
 * Nothing here is stored. Every figure is read off the board, the day's missed
 * walk-ins and who was in — so the summary cannot drift from the grid it is
 * summarising.
 */
import * as Vue from 'vue';
import { store } from '../store.js';

export const EndDay = {
  name: 'EndDay',
  setup: function () {
    var open = Vue.computed(function () { return store.ui.endDay; });
    var d = store.daySummary;

    var dateLabel = Vue.computed(function () {
      var p = String(d.value.businessDate).split('-');
      var dt = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
      return dt.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
    });

    // The headline row. Everything in it is a number the desk would otherwise
    // work out by looking at the grid and counting.
    var tiles = Vue.computed(function () {
      var s = d.value;
      var out = [
        { key: 'served', n: s.served, label: s.served === 1 ? 'service' : 'services',
          note: store.formatCredit(s.credit) + (s.credit === 1 ? ' turn' : ' turns') },
        // In services, not clients: the split is about where the work came
        // from, and one visit can be two squares.
        { key: 'clients', n: s.clients, label: s.clients === 1 ? 'client' : 'clients',
          note: s.booked + ' of ' + s.served + ' booked' },
        { key: 'gap', n: s.gapMs == null ? '—' : store.formatMins(s.gapMs),
          label: 'between rounds',
          note: s.rounds > 1 ? s.rounds + ' turn rounds' : 'one round' },
        { key: 'avg', n: s.avgServiceMs == null ? '—' : store.formatDuration(s.avgServiceMs),
          label: 'average service',
          note: s.longest ? 'longest ' + store.formatDuration(s.longest.ms) : '' }
      ];
      out.push({
        key: 'missed', n: s.missedPeople, label: s.missedPeople === 1 ? 'walk-in missed' : 'walk-ins missed',
        note: s.missed.length ? 'across ' + s.missed.length +
          (s.missed.length === 1 ? ' party' : ' parties') : 'nobody turned away',
        warn: s.missedPeople > 0
      });
      out.push({
        key: 'floor', n: s.onFloor, label: s.onFloor === 1 ? 'tech in' : 'techs in',
        note: s.spread
          ? (s.spread.gap === 0 ? 'all level on turns'
            : store.formatCredit(s.spread.gap) + ' apart, top to bottom')
          : ''
      });
      return out;
    });

    // "3 PM — 6 services, 2 missed". The one place the two halves of the day
    // are read against each other, which is the whole question behind logging
    // a missed walk-in: were we short-handed, and when.
    var busiestLabel = Vue.computed(function () {
      var b = d.value.busiest;
      if (!b) return '';
      return b.label + ' — ' + b.turns + (b.turns === 1 ? ' service' : ' services');
    });

    // One whole string: the compiler eats the space between a {{ }} and the
    // literal text next to it, and it reads "3services are still running".
    var runningLine = Vue.computed(function () {
      var n = d.value.running;
      if (!n) return '';
      return n === 1
        ? '1 service is still running and will be stopped now.'
        : n + ' services are still running and will be stopped now.';
    });

    var closeLabel = Vue.computed(function () {
      var n = d.value.running;
      return n ? 'Stop ' + n + ' and close the day' : 'Close the day';
    });

    var spanLabel = Vue.computed(function () {
      var s = d.value;
      if (s.firstAt == null) return 'Nothing on the board yet';
      return store.formatClock(s.firstAt) + ' – ' + store.formatClock(s.lastAt) +
        (s.openMs ? ' · ' + store.formatDuration(s.openMs) : '');
    });

    function partyLabel(row) {
      return row.partySize === 1 ? '1 person' : row.partySize + ' people';
    }

    // Time in service over the services that finished. Not to be confused with
    // the idle column beside it, which is measured rather than derived from
    // the day's span — see `idleMsFor`. Deriving it from the span would
    // overstate the idle of whoever started at noon.
    function avgOf(rec) {
      if (!rec.turns || !rec.serviceMs) return '—';
      return store.formatDuration(rec.serviceMs / rec.turns);
    }

    // A dash for "no answer" — nobody was idle before the day's first service,
    // and somebody never marked in has no day to be idle in. Whole minutes:
    // the seconds on an hours-long figure are noise, and this is a number read
    // across a table of technicians rather than off a running clock.
    function idleOf(rec) {
      return rec.idleMs == null ? '—' : store.formatMins(rec.idleMs);
    }

    // What it cost the floor, as one line over the table. Person-time: two
    // technicians waiting an hour is two hours of somebody paid to wait.
    var idleTotal = Vue.computed(function () {
      return d.value.idleMs ? store.formatMins(d.value.idleMs) : '';
    });

    return {
      open: open,
      step: Vue.computed(function () { return (open.value && open.value.step) || 'confirm'; }),
      d: d,
      tiles: tiles,
      dateLabel: dateLabel,
      runningLine: runningLine,
      closeLabel: closeLabel,
      busiestLabel: busiestLabel,
      spanLabel: spanLabel,
      partyLabel: partyLabel,
      avgOf: avgOf,
      idleOf: idleOf,
      idleTotal: idleTotal,
      closed: store.dayClosed,
      close: store.closeEndDay,
      ui: store.ui,
      endDay: store.endDay,
      reopen: store.reopenDay,
      formatClock: store.formatClock,
      waitLabel: store.missedWaitLabel,
      formatDuration: store.formatDuration,
      formatCredit: store.formatCredit
    };
  },
  template: [
    '<div v-if="open" class="modal-scrim" @click.self="close">',
    '  <div class="sheet sheet--report" role="dialog" aria-modal="true">',

    /* ---- step one: the question ---- */
    '    <template v-if="step === \'confirm\'">',
    '      <div class="sheet__head sheet__head--plain">',
    '        <div>',
    '          <h3>End the day?</h3>',
    '          <p>{{ dateLabel }}</p>',
    '        </div>',
    '      </div>',
    '      <div class="assign__section">',
    '        <p class="warn-body">',
    '          <strong v-if="runningLine">{{ runningLine }}</strong>',
    '          The board keeps every square exactly as it is, and the day stays in History.',
    '          Nothing else can be put up until the day is reopened, which is one click.',
    '        </p>',
    '      </div>',
    '      <div class="assign__section">',
    '        <h4 class="assign__label">Where the day stands</h4>',
    '        <ul class="day-facts">',
    '          <li><span>{{ d.served }} {{ d.served === 1 ? "service" : "services" }}</span>',
    '            <strong>{{ formatCredit(d.credit) }} {{ d.credit === 1 ? "turn" : "turns" }}</strong></li>',
    '          <li><span>Clients seen</span><strong>{{ d.clients }}</strong></li>',
    '          <li><span>Missed walk-ins</span><strong>{{ d.missedPeople || "none" }}</strong></li>',
    '          <li><span>On the board</span><strong>{{ spanLabel }}</strong></li>',
    '        </ul>',
    '      </div>',
    '      <div class="sheet__actions">',
    '        <button type="button" class="btn btn--primary btn--lg" @click="endDay">{{ closeLabel }}</button>',
    '        <button type="button" class="btn btn--quiet" @click="close">Not yet</button>',
    '      </div>',
    '    </template>',

    /* ---- step two: the day ---- */
    '    <template v-else>',
    '      <div class="sheet__head sheet__head--plain">',
    '        <div>',
    '          <h3>{{ dateLabel }}</h3>',
    '          <p v-if="d.closedAt">Book closed at {{ formatClock(d.closedAt) }} &middot; {{ spanLabel }}</p>',
    '          <p v-else>{{ spanLabel }}</p>',
    '        </div>',
    '        <span v-if="closed" class="sheet__tag">Closed</span>',
    '      </div>',
    '',
    '      <div class="day-tiles">',
    '        <div v-for="t in tiles" :key="t.key" class="day-tile" :class="{ \'is-warn\': t.warn }">',
    '          <strong>{{ t.n }}</strong>',
    '          <span class="day-tile__label">{{ t.label }}</span>',
    '          <span v-if="t.note" class="day-tile__note">{{ t.note }}</span>',
    '        </div>',
    '      </div>',
    '',
    /* The day hour by hour. Services started as a bar, people turned away as a
       second bar under it in the danger colour — read together or not at all. */
    '      <div v-if="d.hours.length" class="assign__section">',
    '        <h4 class="assign__label">Hour by hour',
    '          <span v-if="busiestLabel" class="day-note">busiest {{ busiestLabel }}</span></h4>',
    '        <ul class="day-hours">',
    '          <li v-for="h in d.hours" :key="h.hour">',
    '            <span class="day-hours__label">{{ h.label }}</span>',
    '            <span class="day-hours__track">',
    '              <span class="day-hours__bar" :style="{ width: (h.share * 100) + \'%\' }"></span>',
    '              <span v-if="h.missed" class="day-hours__bar day-hours__bar--missed"',
    '                :style="{ width: (h.missedShare * 100) + \'%\' }"></span>',
    '            </span>',
    '            <span class="day-hours__n">{{ h.turns || "—" }}',
    '              <span v-if="h.missed" class="day-hours__missed"',
    '                :title="h.missed + \' turned away this hour\'">&minus;{{ h.missed }}</span></span>',
    '          </li>',
    '        </ul>',
    '      </div>',
    '',
    '      <div class="assign__section">',
    '        <h4 class="assign__label">Missed walk-ins',
    '          <span v-if="d.missedPeople" class="day-note">{{ d.missedPeople }} turned away</span></h4>',
    '        <p v-if="!d.missed.length" class="setting__hint">Nobody was turned away today.</p>',
    '        <ul v-else class="missed-log">',
    '          <li v-for="row in d.missed" :key="row.id">',
    '            <span class="missed-log__time">{{ formatClock(row.at) }}</span>',
    '            <span class="missed-log__main">',
    '              <span class="missed-log__n">{{ partyLabel(row) }}</span>',
    '              <span class="missed-log__svc">',
    '                {{ row.services.length ? row.services.join(", ") : "no service given" }}</span>',
    '            </span>',
    '            <span class="missed-log__wait" :class="{ \'is-none\': row.waitMinutes == null }">',
    '              {{ waitLabel(row.waitMinutes) }}</span>',
    '          </li>',
    '        </ul>',
    '      </div>',
    '',
    '      <div v-if="d.staff.length" class="assign__section">',
    '        <h4 class="assign__label">By technician',
    '          <span v-if="idleTotal" class="day-note">{{ idleTotal }} idle across the floor</span></h4>',
    '        <div class="table-scroll">',
    '          <table class="report">',
    '            <thead>',
    '              <tr><th>Technician</th><th>Services</th><th>Turns</th>',
    '                <th v-if="ui.requestsOn">Requests</th>',
    /* Beside In service on purpose: the two are the day split in half, and the
       pair is what the question "was the rotation fair" is actually read off.
       The title carries the rules, because a number this easy to argue with
       has to say what it counted. */
    '                <th>In service</th>',
    '                <th title="On the floor with nobody in the chair. Counted from the day&rsquo;s first service, or from when they were marked in; stops while they are out of the rotation, and when the book is shut.">Idle</th>',
    '                <th>Average</th><th>Last finish</th></tr>',
    '            </thead>',
    '            <tbody>',
    '              <tr v-for="rec in d.staff" :key="rec.staffId">',
    '                <td class="report__name">{{ rec.name }}</td>',
    '                <td>{{ rec.turns || "—" }}</td>',
    '                <td>{{ formatCredit(rec.credit) }}</td>',
    '                <td v-if="ui.requestsOn">{{ rec.requests || "—" }}</td>',
    '                <td>{{ rec.serviceMs ? formatDuration(rec.serviceMs) : "—" }}</td>',
    '                <td>{{ idleOf(rec) }}</td>',
    '                <td>{{ avgOf(rec) }}</td>',
    '                <td>{{ rec.lastEnd ? formatClock(rec.lastEnd) : "—" }}</td>',
    '              </tr>',
    '            </tbody>',
    '          </table>',
    '        </div>',
    '      </div>',
    '',
    '      <div v-if="d.mix.length" class="assign__section">',
    '        <h4 class="assign__label">What was done',
    '          <span v-if="ui.requestsOn && d.requests" class="day-note">{{ d.requests }}',
    '            {{ d.requests === 1 ? "request" : "requests" }}</span></h4>',
    '        <ul class="day-mix">',
    '          <li v-for="m in d.mix" :key="m.name">',
    '            <span class="day-mix__letter" :style="{ background: m.color }">{{ m.letter }}</span>',
    '            <span class="day-mix__name">{{ m.name }}</span>',
    '            <span class="day-mix__n">{{ m.turns }}</span>',
    '          </li>',
    '        </ul>',
    '      </div>',
    '',
    '      <div class="sheet__actions">',
    '        <button type="button" class="btn btn--primary" @click="close">Done</button>',
    '        <button v-if="closed" type="button" class="btn btn--quiet" @click="reopen">',
    '          Reopen the day</button>',
    '      </div>',
    '    </template>',
    '  </div>',
    '</div>'
  ].join('\n')
};
